import type { ColDef } from "ag-grid-community";
import type { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { convertToTitleCase, formatCurrency, formatDateByType } from "../../../libs/utils";
import { ConfirmGridModal, createRemoveRowColumn } from "../../../molecules/ConfirmGridModal";
import { ClearingDatePicker, isClearingDateValid } from "./ClearingDatePicker";
import { getReceiptBulkRowKey } from "./utils";

export type BulkClearedReceiptRow = {
  paymentReceiptId?: string;
  receiptNo?: string;
  invoiceNumber?: string;
  invoiceNo?: string;
  propertyDetails?: { propertyName?: string };
  propertyName?: string;
  tenantDetails?: { tenantNameEN?: string };
  tenantName?: string;
  unitCode?: string;
  modeOfPayment?: string;
  paymentMode?: string;
  paymentStatus?: string;
  receiptStatus?: string;
  isCleared?: boolean;
  totalAmount?: number;
  pdc?: {
    chequeNumber?: string;
  };
  paymentDetails?: {
    documentNo?: string;
    depositDate?: string;
  };
  _id?: string;
  beneficiary?: {
    accountTitle?: string;
    accountNumber?: string;
    bankName?: string;
    iban?: string;
  };
};

type BulkClearedReceiptsConfirmProps = {
  receipts: BulkClearedReceiptRow[];
  onConfirm: (paymentReceiptIds: string[], clearedDate: string) => void | Promise<unknown>;
  onClose: () => void;
  isLoading?: boolean;
};

export const BulkClearedReceiptsConfirm = (props: BulkClearedReceiptsConfirmProps) => {
  const { receipts, onConfirm, onClose, isLoading } = props;

  const [rows, setRows] = useState<BulkClearedReceiptRow[]>(() => receipts);
  const [clearingDate, setClearingDate] = useState<Dayjs | null>(null);
  const [showClearingDateError, setShowClearingDateError] = useState(false);

  useEffect(() => {
    setRows(receipts);
    setClearingDate(null);
    setShowClearingDateError(false);
  }, [receipts]);

  const removeRow = useCallback((rowKey: string) => {
    setRows((prev) => prev.filter((r) => getReceiptBulkRowKey(r) !== rowKey));
  }, []);

  const data = useMemo(
    () =>
      rows.map((row) => ({
        rowKey: getReceiptBulkRowKey(row),
        receiptNo: row.receiptNo ?? "",
        invoiceNumber: row.invoiceNumber ?? row.invoiceNo ?? "",
        propertyName: row.propertyDetails?.propertyName ?? row.propertyName ?? "",
        tenantName: row.tenantDetails?.tenantNameEN ?? row.tenantName ?? "",
        unitCode: row.unitCode ?? "",
        chequeNo: row?.pdc?.chequeNumber ?? "",
        paymentRef: row?.paymentDetails?.documentNo ?? "",
        paymentMode: row.modeOfPayment ?? row.paymentMode ?? "",
        clearedStatus: row.isCleared === true ? "Cleared" : "Not Cleared",
        receiptStatus: row.receiptStatus ?? row.paymentStatus ?? "",
        totalAmount: row.totalAmount,
        beneficiaryName: row?.beneficiary?.accountTitle ?? "",
        beneficiaryAccount: row?.beneficiary?.accountNumber ?? "",
        beneficiaryBank: row?.beneficiary?.bankName ?? "",
        beneficiaryIban: row?.beneficiary?.iban ?? "",
      })),
    [rows],
  );

  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: "receiptNo",
        headerName: "Receipt No",
        flex: 0.85,
        minWidth: 150,
        maxWidth: 150,
        valueFormatter: ({ value }: { value?: string }) => (value ? `Rec_${value}` : "--"),
      },
      {
        field: "invoiceNumber",
        headerName: "Invoice No",
        flex: 0.9,
        minWidth: 150,
        maxWidth: 150,
      },
      { field: "propertyName", headerName: "Property", flex: 1, minWidth: 150 },
      { field: "tenantName", headerName: "Tenant Name", flex: 1.2, minWidth: 150 },
      { field: "unitCode", headerName: "Unit Code", flex: 0.85, maxWidth: 200 },
      {
        field: "paymentMode",
        headerName: "Payment Mode",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "chequeNo",
        headerName: "Cheque No",
        flex: 1,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        field: "paymentRef",
        headerName: "Payment Ref",
        flex: 1,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        field: "receiptStatus",
        headerName: "Receipt Status",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "beneficiaryName",
        headerName: "Beneficiary Name",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "beneficiaryAccount",
        headerName: "Beneficiary Account",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "beneficiaryBank",
        headerName: "Beneficiary Bank",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "beneficiaryIban",
        headerName: "Beneficiary IBAN",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "totalAmount",
        headerName: "Total Amount",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        type: "rightAligned",
        valueFormatter: ({ value }: { value?: number }) => formatCurrency(value),
        cellStyle: { textAlign: "right" },
      },
      createRemoveRowColumn({
        field: "rowKey",
        onRemove: removeRow,
        ariaLabel: "Remove from clear list",
      }),
    ],
    [removeRow],
  );

  const handleClearingDateChange = (date: Dayjs | null) => {
    setClearingDate(date);
    if (isClearingDateValid(date)) {
      setShowClearingDateError(false);
    }
  };

  const handleConfirm = () => {
    if (!isClearingDateValid(clearingDate)) {
      setShowClearingDateError(true);
      return;
    }

    const clearedDate = formatDateByType(clearingDate, "api")!;
    const paymentReceiptIds = rows.map((r) => getReceiptBulkRowKey(r)).filter(Boolean);
    void onConfirm(paymentReceiptIds, clearedDate);
  };

  return (
    <ConfirmGridModal>
      {rows.length > 0 && (
        <ConfirmGridModal.SubText>
          The {rows.length} receipt{rows.length === 1 ? "" : "s"} below will be marked as cleared. Remove any you do not
          want to include before confirming.
        </ConfirmGridModal.SubText>
      )}
      <ConfirmGridModal.Grid
        columns={columns}
        data={data}
        isEmpty={rows.length === 0}
        emptyMessage="No receipts left in this list. Close the dialog and select again."
        className="bulk-cleared-receipts-confirm-grid"
      />
      <ConfirmGridModal.Actions
        layout="split"
        gap={16}
        style={{ marginTop: "3.5rem" }}
        secondaryButton={{
          label: "Cancel",
          onClick: onClose,
          disabled: isLoading,
        }}
        accessory={
          <ClearingDatePicker
            value={clearingDate}
            onChange={handleClearingDateChange}
            showValidation={showClearingDateError}
          />
        }
        primaryButton={{
          label: "Confirm and Clear",
          onClick: handleConfirm,
          disabled: rows.length === 0,
          loading: isLoading,
        }}
      />
    </ConfirmGridModal>
  );
};
